import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonClasses } from '@/components/generals/button'
import { navLinks, siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!isOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [isOpen])

    const closeMenu = () => setIsOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b transition-[background-color,border-color,box-shadow] duration-300',
                isScrolled || isOpen
                    ? 'border-border/70 bg-background/90 shadow-sm shadow-broth/5 backdrop-blur-md'
                    : 'border-transparent bg-background/0'
            )}
        >
            <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 sm:px-8 lg:h-20'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name} home`}
                    className='rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background'
                >
                    <Logo />
                </Link>

                <nav aria-label="Main" className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-sm font-medium text-foreground/75 outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href={siteConfig.phoneHref}
                        className={buttonClasses('ghost', 'sm', 'hidden xl:inline-flex')}
                    >
                        <Phone />
                        {siteConfig.phone}
                    </a>

                    <Link
                        href='/#order'
                        className={buttonClasses('primary', 'sm', 'hidden sm:inline-flex')}
                    >
                        Order ahead
                    </Link>

                    <button
                        type='button'
                        onClick={() => setIsOpen(open => !open)}
                        aria-expanded={isOpen}
                        aria-controls='mobile-menu'
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                        className={buttonClasses('outline', 'icon-sm', 'lg:hidden')}
                    >
                        {isOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                hidden={!isOpen}
                className='border-t border-border/70 bg-background animate-in fade-in slide-in-from-top-2 duration-200 lg:hidden'
            >
                <nav aria-label="Mobile" className='mx-auto max-w-6xl px-5 pb-6 pt-3 sm:px-8'>
                    <ul className='flex flex-col'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={closeMenu}
                                    className='flex rounded-xl px-3 py-3 font-heading text-xl font-medium outline-none transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-4 grid gap-2 border-t border-border/70 pt-5 sm:grid-cols-2'>
                        <Link
                            href='/#order'
                            onClick={closeMenu}
                            className={buttonClasses('primary', 'md')}
                        >
                            Order ahead for pickup
                        </Link>

                        <a
                            href={siteConfig.phoneHref}
                            className={buttonClasses('outline', 'md')}
                        >
                            <Phone />
                            Call {siteConfig.phone}
                        </a>
                    </div>
                </nav>
            </div>
        </header>
    )
}
